import { ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { LayoutDashboard, Users, Gift, BarChart3, Settings, CreditCard, Menu, LogOut } from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useToast } from "@/hooks/use-toast";
import { useBrand } from "@/hooks/useBrand";
import { Alert, AlertDescription } from "@/components/ui/alert";

const navigation = [
  { name: "Overview", href: "/dashboard", icon: LayoutDashboard, end: true },
  { name: "Referrals", href: "/dashboard/referrals", icon: Users },
  { name: "Rewards", href: "/dashboard/rewards", icon: Gift },
  { name: "Analytics", href: "/dashboard/analytics", icon: BarChart3 },
  { name: "Settings", href: "/dashboard/settings", icon: Settings },
  { name: "Billing", href: "/dashboard/billing", icon: CreditCard },
];

interface DashboardLayoutProps {
  children: ReactNode;
}

export const DashboardLayout = ({ children }: DashboardLayoutProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { data: brand } = useBrand();

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/auth");
      }
    };
    checkAuth();
  }, [navigate]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    toast({ title: "Signed out successfully" });
    navigate("/");
  };

  const getTrialStatus = () => {
    if (!brand || brand.subscription_status !== "trialing") return null;
    
    const trialEnd = new Date(brand.trial_end_at);
    const now = new Date();
    const daysLeft = Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysLeft <= 0) return { expired: true, daysLeft: 0 };
    return { expired: false, daysLeft };
  };

  const trialStatus = getTrialStatus();

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <div className="lg:hidden flex items-center justify-between p-4 border-b">
        <span className="text-xl font-bold">Refertle</span>
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-64">
            <SheetHeader>
              <SheetTitle>Menu</SheetTitle>
            </SheetHeader>
            <nav className="flex flex-col gap-2 mt-6">
              {navigation.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  end={item.end}
                  className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-accent transition-colors"
                  activeClassName="bg-accent text-accent-foreground"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <item.icon className="h-5 w-5" />
                  {item.name}
                </NavLink>
              ))}
              <Button
                variant="ghost"
                className="justify-start gap-3 px-3"
                onClick={handleSignOut}
              >
                <LogOut className="h-5 w-5" />
                Sign Out
              </Button>
            </nav>
          </SheetContent>
        </Sheet>
      </div>

      <div className="flex">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 border-r bg-card">
          <div className="flex flex-col flex-1 min-h-0">
            <div className="flex items-center h-16 px-6 border-b">
              <span className="text-2xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                Refertle
              </span>
            </div>
            <nav className="flex-1 px-3 py-4 space-y-1">
              {navigation.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  end={item.end}
                  className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-accent transition-colors"
                  activeClassName="bg-accent text-accent-foreground font-medium"
                >
                  <item.icon className="h-5 w-5" />
                  {item.name}
                </NavLink>
              ))}
            </nav>
            <div className="p-3 border-t">
              <Button
                variant="ghost"
                className="w-full justify-start gap-3"
                onClick={handleSignOut}
              >
                <LogOut className="h-5 w-5" />
                Sign Out
              </Button>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="lg:pl-64 flex-1">
          <div className="p-6 lg:p-8 max-w-7xl mx-auto">
            {/* Trial Status Banner */}
            {trialStatus && (
              <Alert className="mb-6">
                <AlertDescription>
                  {trialStatus.expired ? (
                    <>
                      Your 14-day trial for the{" "}
                      <strong>{brand?.current_plan?.name || "Free"} plan</strong> has
                      ended. Continue with full access for{" "}
                      <strong>₹{brand?.current_plan?.monthly_price_inr}/month</strong>.{" "}
                      <Button
                        variant="link"
                        className="p-0 h-auto"
                        onClick={() => navigate("/dashboard/billing")}
                      >
                        Go to Billing
                      </Button>
                    </>
                  ) : (
                    <>
                      You're on a 14-day free trial of the{" "}
                      <strong>{brand?.current_plan?.name || "Free"} plan</strong>. Trial
                      ends in <strong>{trialStatus.daysLeft} days</strong>.
                    </>
                  )}
                </AlertDescription>
              </Alert>
            )}
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};